<template>
  <section class="candidates-table-shell settings-panel">
    <div class="settings-org-section">
      <SettingsSectionHeader
        title="Users"
        subtitle="Drivers app accounts and role assignment"
        icon="pi-users"
      >
        <template #actions>
          <Button
            label="Add User"
            icon="pi pi-plus"
            size="small"
            :disabled="saving"
            @click="openCreate"
          />
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            size="small"
            severity="secondary"
            outlined
            :loading="loading"
            @click="loadUsers"
          />
        </template>
      </SettingsSectionHeader>

      <div class="settings-org-section__body">
        <div v-if="loading && !users.length" class="settings-org-section__state">
          <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
        </div>

        <Message
          v-else-if="error && !users.length"
          severity="error"
          :closable="false"
          class="settings-org-section__message"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-3">
            <span class="flex-1">{{ error }}</span>
            <Button label="Retry" size="small" severity="danger" outlined @click="loadUsers" />
          </div>
        </Message>

        <template v-else>
          <SettingsOrgReferenceToolbar
            v-model:search-query="searchQuery"
            :count-label="countLabel"
            search-placeholder="Search users..."
            :loading="loading"
          />

          <div v-if="!filteredUsers.length" class="settings-org-section__empty">
            <i class="pi pi-users settings-org-section__empty-icon" aria-hidden="true" />
            <p>{{ searchQuery ? 'No users match your search.' : 'No users yet.' }}</p>
            <Button
              v-if="searchQuery"
              label="Clear search"
              size="small"
              severity="secondary"
              text
              @click="searchQuery = ''"
            />
          </div>

          <DataTable
            v-else
            :value="filteredUsers"
            size="small"
            dataKey="id"
            sort-mode="single"
            removable-sort
            class="candidates-table"
          >
            <Column field="fullName" header="Name" sortable style="min-width: 10rem">
              <template #body="{ data }">
                <span class="font-medium">{{ data.fullName }}</span>
              </template>
            </Column>
            <Column field="username" header="Username" sortable style="min-width: 8rem">
              <template #body="{ data }">
                <span class="text-sm font-mono text-surface-600 dark:text-surface-400">
                  {{ data.username }}
                </span>
              </template>
            </Column>
            <Column field="email" header="Email" sortable style="min-width: 12rem">
              <template #body="{ data }">
                <span class="text-sm">{{ data.email }}</span>
              </template>
            </Column>
            <Column field="role" header="Role" sortable style="width: 8rem">
              <template #body="{ data }">
                <Tag
                  :value="driversRoleLabel(data.role)"
                  :severity="driversRoleSeverity(data.role)"
                  rounded
                />
              </template>
            </Column>
            <Column field="active" header="Status" sortable style="width: 7rem">
              <template #body="{ data }">
                <Tag
                  :value="data.active ? 'Active' : 'Disabled'"
                  :severity="data.active ? 'success' : 'secondary'"
                  rounded
                />
              </template>
            </Column>
            <Column header="Actions" style="width: 6rem">
              <template #body="{ data }">
                <div class="crc-table-row-actions">
                  <Button
                    icon="pi pi-pencil"
                    size="small"
                    severity="secondary"
                    outlined
                    rounded
                    v-tooltip.top="'Edit'"
                    :disabled="saving"
                    @click="openEdit(data)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </div>
    </div>

    <UserEditModal ref="modalRef" v-model:visible="modalVisible" :saving="saving" @save="onSave" />
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import { useToast } from 'primevue/usetoast';
import SettingsSectionHeader from './SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from './SettingsOrgReferenceToolbar.vue';
import UserEditModal from './modals/UserEditModal.vue';
import { useSettingsUsers } from '../composables/useSettingsUsers';
import { driversRoleLabel, driversRoleSeverity } from '../utils/driversRoleLabels';

defineOptions({ name: 'SettingsUsersSection' });

const toast = useToast();
const modalRef = ref(null);
const modalVisible = ref(false);

const {
  loading,
  saving,
  error,
  users,
  searchQuery,
  filteredUsers,
  countLabel,
  loadUsers,
  saveUser
} = useSettingsUsers();

function openCreate() {
  modalRef.value?.openCreate();
}

function openEdit(user) {
  modalRef.value?.openEdit(user);
}

async function onSave(payload) {
  try {
    await saveUser(payload);
    modalVisible.value = false;
    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: payload.id ? 'User updated.' : 'User created.',
      life: 2500
    });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e?.message || 'Failed to save user.',
      life: 4000
    });
  }
}

onMounted(() => {
  loadUsers();
});

defineExpose({ reload: loadUsers });
</script>
